import { supabase } from "@/integrations/supabase/client";

// Declare fbq as a global
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

// Debug logging — silent in production
const FB_DEBUG = import.meta.env.DEV;
function fbDebug(...args: unknown[]) {
  if (FB_DEBUG) console.log('[FB]', ...args);
}

// ============================================
// VISITOR IDENTIFICATION
// ============================================

// Get or create a persistent visitor ID for external_id matching
export function getOrCreateVisitorId(): string {
  const storageKey = '_fb_external_id';
  let visitorId = localStorage.getItem(storageKey);
  
  if (!visitorId) {
    visitorId = 'v_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
    localStorage.setItem(storageKey, visitorId);
  }
  
  return visitorId;
}

// ============================================
// COUNTRY DETECTION
// ============================================

// Timezone to ISO 3166-1 alpha-2 country code mapping
const timezoneToCountry: Record<string, string> = {
  // North America — US (all IANA zones)
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Los_Angeles': 'US', 'America/Anchorage': 'US', 'America/Phoenix': 'US',
  'America/Detroit': 'US', 'America/Boise': 'US', 'America/Juneau': 'US',
  'America/Adak': 'US', 'America/Nome': 'US', 'America/Sitka': 'US',
  'America/Yakutat': 'US', 'America/Menominee': 'US', 'America/Metlakatla': 'US',
  'America/Indiana/Indianapolis': 'US', 'America/Indiana/Knox': 'US',
  'America/Indiana/Marengo': 'US', 'America/Indiana/Petersburg': 'US',
  'America/Indiana/Tell_City': 'US', 'America/Indiana/Vevay': 'US',
  'America/Indiana/Vincennes': 'US', 'America/Indiana/Winamac': 'US',
  'America/Kentucky/Louisville': 'US', 'America/Kentucky/Monticello': 'US',
  'America/North_Dakota/Beulah': 'US', 'America/North_Dakota/Center': 'US',
  'America/North_Dakota/New_Salem': 'US', 'Pacific/Honolulu': 'US',
  'US/Eastern': 'US', 'US/Central': 'US', 'US/Mountain': 'US',
  'US/Pacific': 'US', 'US/Alaska': 'US', 'US/Hawaii': 'US',
  // North America — Canada (all IANA zones)
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Montreal': 'CA',
  'America/Edmonton': 'CA', 'America/Winnipeg': 'CA', 'America/Halifax': 'CA',
  'America/St_Johns': 'CA', 'America/Regina': 'CA', 'America/Whitehorse': 'CA',
  'America/Yellowknife': 'CA', 'America/Iqaluit': 'CA', 'America/Moncton': 'CA',
  'America/Thunder_Bay': 'CA', 'America/Rankin_Inlet': 'CA',
  'America/Resolute': 'CA', 'America/Glace_Bay': 'CA', 'America/Goose_Bay': 'CA',
  'America/Dawson': 'CA', 'America/Dawson_Creek': 'CA', 'America/Fort_Nelson': 'CA',
  'America/Creston': 'CA', 'America/Nipigon': 'CA', 'America/Rainy_River': 'CA',
  'America/Swift_Current': 'CA', 'America/Atikokan': 'CA', 'America/Blanc-Sablon': 'CA',
  'America/Cambridge_Bay': 'CA', 'America/Inuvik': 'CA', 'America/Pangnirtung': 'CA',
  'Canada/Eastern': 'CA', 'Canada/Central': 'CA', 'Canada/Mountain': 'CA',
  'Canada/Pacific': 'CA', 'Canada/Atlantic': 'CA', 'Canada/Newfoundland': 'CA',
  // Mexico
  'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Merida': 'MX',
  'America/Monterrey': 'MX', 'America/Tijuana': 'MX', 'America/Hermosillo': 'MX',
  'America/Chihuahua': 'MX', 'America/Mazatlan': 'MX',

  // Europe
  'Europe/London': 'GB', 'Europe/Belfast': 'GB', 'GB': 'GB',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES', 'Atlantic/Canary': 'ES',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Copenhagen': 'DK',
  'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL',
  'Europe/Prague': 'CZ',
  'Europe/Budapest': 'HU',
  'Europe/Athens': 'GR',
  'Europe/Lisbon': 'PT', 'Atlantic/Madeira': 'PT', 'Atlantic/Azores': 'PT',
  'Europe/Dublin': 'IE',
  'Europe/Moscow': 'RU', 'Europe/Kaliningrad': 'RU', 'Asia/Yekaterinburg': 'RU',
  'Asia/Novosibirsk': 'RU', 'Asia/Vladivostok': 'RU', 'Asia/Kamchatka': 'RU',
  'Europe/Bucharest': 'RO',
  'Europe/Sofia': 'BG',
  'Europe/Belgrade': 'RS',
  'Europe/Zagreb': 'HR',
  'Europe/Ljubljana': 'SI',
  'Europe/Bratislava': 'SK',
  'Europe/Vilnius': 'LT',
  'Europe/Riga': 'LV',
  'Europe/Tallinn': 'EE',
  'Europe/Kiev': 'UA', 'Europe/Kyiv': 'UA',
  'Europe/Minsk': 'BY',
  'Europe/Luxembourg': 'LU',
  'Europe/Malta': 'MT',
  'Europe/Monaco': 'MC',
  'Europe/Tirane': 'AL',

  // Asia
  'Asia/Jerusalem': 'IL', 'Asia/Tel_Aviv': 'IL',
  'Asia/Tokyo': 'JP',
  'Asia/Seoul': 'KR',
  'Asia/Shanghai': 'CN', 'Asia/Chongqing': 'CN', 'Asia/Urumqi': 'CN',
  'Asia/Hong_Kong': 'HK',
  'Asia/Singapore': 'SG',
  'Asia/Bangkok': 'TH',
  'Asia/Jakarta': 'ID', 'Asia/Makassar': 'ID', 'Asia/Jayapura': 'ID',
  'Asia/Manila': 'PH',
  'Asia/Kuala_Lumpur': 'MY', 'Asia/Kuching': 'MY',
  'Asia/Ho_Chi_Minh': 'VN', 'Asia/Saigon': 'VN',
  'Asia/Taipei': 'TW',
  'Asia/Dubai': 'AE',
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  'Asia/Riyadh': 'SA',
  'Asia/Karachi': 'PK',
  'Asia/Dhaka': 'BD',
  'Asia/Colombo': 'LK',
  'Asia/Kathmandu': 'NP',
  'Asia/Almaty': 'KZ',
  'Asia/Tashkent': 'UZ',
  'Asia/Beirut': 'LB',
  'Asia/Baghdad': 'IQ',
  'Asia/Tehran': 'IR',
  'Asia/Muscat': 'OM',
  'Asia/Qatar': 'QA',
  'Asia/Kuwait': 'KW',
  'Asia/Bahrain': 'BH',
  'Asia/Nicosia': 'CY',
  'Asia/Tbilisi': 'GE',
  'Asia/Yerevan': 'AM',
  'Asia/Baku': 'AZ',

  // Oceania — Australia (all IANA zones)
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU', 'Australia/Hobart': 'AU',
  'Australia/Darwin': 'AU', 'Australia/Lord_Howe': 'AU', 'Australia/Lindeman': 'AU',
  'Australia/Broken_Hill': 'AU', 'Australia/Currie': 'AU', 'Australia/Eucla': 'AU',
  'Australia/ACT': 'AU', 'Australia/Canberra': 'AU', 'Australia/NSW': 'AU',
  'Australia/North': 'AU', 'Australia/Queensland': 'AU', 'Australia/South': 'AU',
  'Australia/Tasmania': 'AU', 'Australia/Victoria': 'AU', 'Australia/West': 'AU',
  // New Zealand
  'Pacific/Auckland': 'NZ', 'Pacific/Chatham': 'NZ', 'NZ': 'NZ',
  // Pacific
  'Pacific/Fiji': 'FJ',
  'Pacific/Guam': 'GU',

  // South America
  'America/Sao_Paulo': 'BR', 'America/Fortaleza': 'BR', 'America/Recife': 'BR',
  'America/Bahia': 'BR', 'America/Manaus': 'BR',
  'America/Buenos_Aires': 'AR', 'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR', 'America/Argentina/Mendoza': 'AR',
  'America/Santiago': 'CL',
  'America/Lima': 'PE',
  'America/Bogota': 'CO',
  'America/Caracas': 'VE',
  'America/Guayaquil': 'EC',
  'America/La_Paz': 'BO',
  'America/Asuncion': 'PY',
  'America/Montevideo': 'UY',

  // Africa
  'Africa/Johannesburg': 'ZA',
  'Africa/Cairo': 'EG',
  'Africa/Lagos': 'NG',
  'Africa/Nairobi': 'KE',
  'Africa/Casablanca': 'MA',
  'Africa/Accra': 'GH',
  'Africa/Dar_es_Salaam': 'TZ',
  'Africa/Addis_Ababa': 'ET',
};

// Language to country fallback mapping
const languageToCountry: Record<string, string> = {
  'en-US': 'US',
  'en-GB': 'GB',
  'en-AU': 'AU',
  'en-CA': 'CA',
  'fr-FR': 'FR',
  'fr-CA': 'CA',
  'de-DE': 'DE',
  'de-AT': 'AT',
  'de-CH': 'CH',
  'es-ES': 'ES',
  'es-MX': 'MX',
  'it-IT': 'IT',
  'pt-BR': 'BR',
  'pt-PT': 'PT',
  'ja-JP': 'JP',
  'ko-KR': 'KR',
  'zh-CN': 'CN',
  'zh-TW': 'TW',
  'zh-HK': 'HK',
  'he-IL': 'IL',
  'hr-HR': 'HR',
  'ar-SA': 'SA',
  'ar-AE': 'AE',
  'ru-RU': 'RU',
  'nl-NL': 'NL',
  'pl-PL': 'PL',
  'sv-SE': 'SE',
  'da-DK': 'DK',
  'fi-FI': 'FI',
  'no-NO': 'NO',
  'th-TH': 'TH',
  'vi-VN': 'VN',
  'id-ID': 'ID',
  'ms-MY': 'MY',
  'tr-TR': 'TR',
};

// Detect country: prefer server-detected (Cloudflare IP geolocation), then timezone, then language
export function detectCountry(): string {
  try {
    // Priority 1: Server-detected country from Cloudflare CF-IPCountry header
    // Stored by FacebookPixel component when get-pixel-config returns countryCode
    // This is the most reliable source — actual IP geolocation, not browser guessing
    const serverCountry = typeof localStorage !== 'undefined' ? localStorage.getItem('_server_country_code') : null;
    if (serverCountry && /^[A-Z]{2}$/.test(serverCountry)) {
      return serverCountry;
    }

    // Priority 2: Browser timezone → country mapping
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone && timezoneToCountry[timezone]) {
      return timezoneToCountry[timezone];
    }

    // Priority 3: Browser language with region (e.g., en-GB → GB)
    const language = navigator.language;
    if (language && languageToCountry[language]) {
      return languageToCountry[language];
    }

    // Priority 4: Base language for non-Latin scripts
    const languageBase = language?.split('-')[0];
    const languageCountryMap: Record<string, string> = {
      'he': 'IL', 'ko': 'KR', 'ja': 'JP', 'zh': 'CN',
      'ar': 'SA', 'ru': 'RU', 'th': 'TH', 'vi': 'VN',
    };
    if (languageBase && languageCountryMap[languageBase]) {
      return languageCountryMap[languageBase];
    }

    return 'US';
  } catch {
    return 'US';
  }
}

// ============================================
// COOKIE UTILITIES
// ============================================

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue || null;
  }
  return null;
}

function getRootDomain(): string {
  const hostname = window.location.hostname;
  // IP address or localhost — no domain attribute needed
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname === 'localhost') return '';
  const parts = hostname.split('.');
  // e.g. pdrn-skin.com → .pdrn-skin.com; sub.pdrn-skin.com → .pdrn-skin.com
  return parts.length >= 2 ? '.' + parts.slice(-2).join('.') : '';
}

function setCookie(name: string, value: string, days: number) {
  const maxAge = days * 24 * 60 * 60;
  const rootDomain = getRootDomain();
  // Public suffix domains (.app, .dev, etc.) — skip domain attribute to avoid cookie blocking
  // Custom domains like .pdrn-skin.com get domain attribute for cross-subdomain persistence
  const isPublicSuffix = !rootDomain || /\.(app|dev|github\.io|netlify\.app|vercel\.app|pages\.dev)$/i.test(rootDomain);
  const domainPart = isPublicSuffix ? '' : `; domain=${rootDomain}`;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${domainPart}`;
}

// ============================================
// EVENT ID GENERATION
// ============================================

export async function generateEventId(eventName: string, identifier: string = ''): Promise<string> {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  const base = `${eventName}_${identifier}_${timestamp}_${random}`;
  
  const msgBuffer = new TextEncoder().encode(base);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 36);
}

export async function generateDeterministicEventId(eventName: string, orderId: string, timestamp: number): Promise<string> {
  const base = `${eventName}_${orderId}_${timestamp}`;
  
  const msgBuffer = new TextEncoder().encode(base);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 36);
}

// ============================================
// FBC/FBP COOKIE MANAGEMENT (Dual Persistence)
// Cookie + localStorage backup for resilience
// ============================================

const LS_FBC_BACKUP = '_fb_fbc_backup';
const LS_FBP_BACKUP = '_fb_fbp_backup';
const LS_LAST_FBCLID = '_fb_last_fbclid';

// Save to localStorage safely
function lsSet(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* silent */ }
}

// Read from localStorage safely
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

/**
 * Initialize FBC from URL fbclid parameter.
 * Runs on every route change to capture fbclid from any entry page.
 * Uses dual persistence: cookie + localStorage backup.
 */
export function initializeFBC(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  let fbclid = urlParams.get('fbclid');

  // Also check URL hash (some redirects place fbclid there)
  if (!fbclid && window.location.hash) {
    try {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      fbclid = hashParams.get('fbclid');
    } catch { /* invalid hash format */ }
  }

  // Fallback: extract fbclid from document.referrer (covers redirect chains)
  if (!fbclid && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      fbclid = referrerUrl.searchParams.get('fbclid');
    } catch { /* invalid referrer URL */ }
  }
  
  // Check for Facebook referrer without fbclid (stories, reels, messenger redirects)
  let detectedFacebookWithoutFbclid = false;
  if (!fbclid && document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      const isFacebookReferrer = /(\.|^)(facebook\.com|fbcdn\.net|fb\.com|messenger\.com|instagram\.com)$/i.test(referrerHost);
      if (isFacebookReferrer) {
        fbDebug('Facebook referrer detected without fbclid:', referrerHost);
        detectedFacebookWithoutFbclid = true;
        // Try to extract fbclid from referrer search params (some encode it differently)
        try {
          const refUrl = new URL(document.referrer);
          const refFbclid = refUrl.searchParams.get('fbclid') || refUrl.searchParams.get('h');
          if (refFbclid) {
            fbclid = refFbclid;
            detectedFacebookWithoutFbclid = false;
          }
        } catch { /* silent */ }
      }
    } catch { /* invalid referrer */ }
  }

  // Check for Facebook in-app browser UA without fbclid
  if (!fbclid && /FBAN|FBAV|Instagram/i.test(navigator.userAgent)) {
    detectedFacebookWithoutFbclid = true;
    fbDebug('Facebook in-app browser detected without fbclid');
  }

  // If we detected Facebook traffic without fbclid, ensure traffic_source is set correctly
  // (prevents race condition where detectTrafficSource() might miss the referrer/UA)
  if (detectedFacebookWithoutFbclid) {
    try {
      const currentSource = sessionStorage.getItem('_traffic_source');
      if (!currentSource || currentSource === 'direct') {
        sessionStorage.setItem('_traffic_source', 'facebook_paid');
        fbDebug('Traffic source forced to facebook_paid (FB signal without fbclid)');
      }
    } catch { /* silent */ }
  }

  // If fbclid found — always save it (freshest click)
  if (fbclid) {
    lsSet(LS_LAST_FBCLID, fbclid);
    // Preserve original click timestamp if available (set by index.html inline script),
    // otherwise use current time. This ensures FBC stays consistent across the session
    // instead of generating a new timestamp on every call.
    const originalTs = lsGet('_fb_fbc_original_ts');
    const clickTs = originalTs || Date.now().toString();
    if (!originalTs) lsSet('_fb_fbc_original_ts', clickTs);
    const fbc = `fb.1.${clickTs}.${fbclid}`;
    setCookie('_fbc', fbc, 90);
    lsSet(LS_FBC_BACKUP, fbc);
    try { sessionStorage.setItem('_fb_fbc_session', fbc); } catch { /* silent */ }
    return fbc;
  }
  
  // No fbclid found — try to read existing fbc from cookie (including SDK-set _fbc)
  const cookieFbc = getCookie('_fbc');
  if (cookieFbc) {
    // Sync cookie → localStorage + sessionStorage backup
    lsSet(LS_FBC_BACKUP, cookieFbc);
    try { sessionStorage.setItem('_fb_fbc_session', cookieFbc); } catch { /* silent */ }
    return cookieFbc;
  }
  
  // Cookie missing — try localStorage backup
  const backupFbc = lsGet(LS_FBC_BACKUP);
  if (backupFbc) {
    // Restore cookie from backup
    setCookie('_fbc', backupFbc, 90);
    return backupFbc;
  }

  // Bug 1.4: Try sessionStorage backup (survives localStorage clearing by ad blockers)
  try {
    const sessionFbc = sessionStorage.getItem('_fb_fbc_session');
    if (sessionFbc) {
      setCookie('_fbc', sessionFbc, 90);
      lsSet(LS_FBC_BACKUP, sessionFbc);
      return sessionFbc;
    }
  } catch { /* silent */ }
  
  // Last resort — try to rebuild fbc from stored fbclid using the ORIGINAL click timestamp.
  // Falling back to Date.now() here would misrepresent the click time and degrade attribution.
  const savedFbclid = lsGet(LS_LAST_FBCLID);
  if (savedFbclid) {
    const originalTs = lsGet('_fb_fbc_original_ts') || Date.now().toString();
    if (!lsGet('_fb_fbc_original_ts')) lsSet('_fb_fbc_original_ts', originalTs);
    const fbc = `fb.1.${originalTs}.${savedFbclid}`;
    setCookie('_fbc', fbc, 90);
    lsSet(LS_FBC_BACKUP, fbc);
    try { sessionStorage.setItem('_fb_fbc_session', fbc); } catch { /* silent */ }
    return fbc;
  }
  
  return null; // Legitimate: user didn't come from a Facebook ad
}

/**
 * Get FBP with localStorage fallback.
 * fbq auto-generates _fbp cookie, but it can be blocked/cleared.
 */
export function getFBP(): string | null {
  const cookieFbp = getCookie('_fbp');
  if (cookieFbp) {
    // Sync to localStorage + sessionStorage backup
    lsSet(LS_FBP_BACKUP, cookieFbp);
    try { sessionStorage.setItem('_fb_fbp_session', cookieFbp); } catch { /* silent */ }
    return cookieFbp;
  }

  // Cookie missing — try localStorage backup
  const lsFbp = lsGet(LS_FBP_BACKUP);
  if (lsFbp) {
    // Restore cookie from backup so browser pixel (fbq) can also use it
    setCookie('_fbp', lsFbp, 90);
    try { sessionStorage.setItem('_fb_fbp_session', lsFbp); } catch { /* silent */ }
    return lsFbp;
  }

  // Last resort — try sessionStorage (survives when ad blockers clear localStorage)
  try {
    const ssFbp = sessionStorage.getItem('_fb_fbp_session');
    if (ssFbp) {
      setCookie('_fbp', ssFbp, 90);
      lsSet(LS_FBP_BACKUP, ssFbp);
      return ssFbp;
    }
  } catch { /* silent */ }

  return null;
}

/**
 * Validate FBC format client-side. Must match fb.1.<digits>.<string>.
 * If malformed but contains fbclid parts, attempts reconstruction.
 */
const CLIENT_FBC_PATTERN = /^fb\.1\.\d+\..+$/;

function isValidFBC(fbc: string | null): string | null {
  if (!fbc || !fbc.trim()) return null;
  const trimmed = fbc.trim();
  if (CLIENT_FBC_PATTERN.test(trimmed)) return trimmed;
  // Try to reconstruct from malformed parts
  const parts = trimmed.split('.');
  if (parts.length >= 4) {
    const fbclid = parts.slice(3).join('.');
    if (fbclid) {
      const reconstructed = `fb.1.${Date.now()}.${fbclid}`;
      fbDebug('Reconstructed malformed FBC:', trimmed, '→', reconstructed);
      return reconstructed;
    }
  }
  fbDebug('Dropping invalid FBC format:', trimmed);
  return null;
}

/**
 * Get FBC with full fallback chain + format validation:
 * cookie → localStorage backup → sessionStorage → fbclid restore
 */
export function getFBC(): string | null {
  const cookieFbc = getCookie('_fbc');
  if (cookieFbc) {
    const valid = isValidFBC(cookieFbc);
    if (valid) {
      lsSet(LS_FBC_BACKUP, valid);
      try { sessionStorage.setItem('_fb_fbc_session', valid); } catch { /* silent */ }
      return valid;
    }
  }

  // Try localStorage backup
  const backupFbc = lsGet(LS_FBC_BACKUP);
  if (backupFbc) {
    const valid = isValidFBC(backupFbc);
    if (valid) {
      setCookie('_fbc', valid, 90);
      return valid;
    }
  }

  // Try sessionStorage (survives when ad blockers clear localStorage mid-session)
  try {
    const sessionFbc = sessionStorage.getItem('_fb_fbc_session');
    if (sessionFbc) {
      const valid = isValidFBC(sessionFbc);
      if (valid) {
        setCookie('_fbc', valid, 90);
        lsSet(LS_FBC_BACKUP, valid);
        return valid;
      }
    }
  } catch { /* silent */ }

  // Try to rebuild from stored fbclid (using original click timestamp if available)
  const savedFbclid = lsGet(LS_LAST_FBCLID);
  if (savedFbclid) {
    const originalTs = lsGet('_fb_fbc_original_ts') || Date.now().toString();
    const fbc = `fb.1.${originalTs}.${savedFbclid}`;
    setCookie('_fbc', fbc, 90);
    lsSet(LS_FBC_BACKUP, fbc);
    return fbc;
  }

  return null;
}

/**
 * Refresh FB cookies — sync fbq-generated cookies to localStorage.
 * Should be called before every event to ensure latest data is captured.
 */
export function refreshFBCookies() {
  const fbp = getCookie('_fbp');
  if (fbp) lsSet(LS_FBP_BACKUP, fbp);

  const fbc = getCookie('_fbc');
  if (fbc) lsSet(LS_FBC_BACKUP, fbc);
}

/**
 * Bidirectional cookie reinforcement — restores cookies from backups
 * if they've been cleared by ad blockers, ITP, or privacy tools.
 *
 * Unlike refreshFBCookies (cookie → localStorage), this also does
 * the reverse (localStorage/sessionStorage → cookie), ensuring cookies
 * survive periodic clearing by ad blockers between events.
 */
function reinforceCookies() {
  // FBP: cookie → backup (normal sync)
  const fbpCookie = getCookie('_fbp');
  if (fbpCookie) {
    lsSet(LS_FBP_BACKUP, fbpCookie);
    try { sessionStorage.setItem('_fb_fbp_session', fbpCookie); } catch { /* silent */ }
  } else {
    // Cookie cleared — restore from backups
    const fbpBackup = lsGet(LS_FBP_BACKUP);
    if (fbpBackup) {
      setCookie('_fbp', fbpBackup, 90);
    } else {
      try {
        const fbpSession = sessionStorage.getItem('_fb_fbp_session');
        if (fbpSession) {
          setCookie('_fbp', fbpSession, 90);
          lsSet(LS_FBP_BACKUP, fbpSession);
        }
      } catch { /* silent */ }
    }
  }

  // FBC: cookie → backup (normal sync)
  const fbcCookie = getCookie('_fbc');
  if (fbcCookie) {
    lsSet(LS_FBC_BACKUP, fbcCookie);
    try { sessionStorage.setItem('_fb_fbc_session', fbcCookie); } catch { /* silent */ }
  } else {
    // Cookie cleared — restore from backups
    const fbcBackup = lsGet(LS_FBC_BACKUP);
    if (fbcBackup) {
      setCookie('_fbc', fbcBackup, 90);
    } else {
      try {
        const fbcSession = sessionStorage.getItem('_fb_fbc_session');
        if (fbcSession) {
          setCookie('_fbc', fbcSession, 90);
          lsSet(LS_FBC_BACKUP, fbcSession);
        }
      } catch { /* silent */ }
      // Last resort: rebuild from stored fbclid
      if (!getCookie('_fbc')) {
        const savedFbclid = lsGet(LS_LAST_FBCLID);
        if (savedFbclid) {
          const originalTs = lsGet('_fb_fbc_original_ts') || Date.now().toString();
          const fbc = `fb.1.${originalTs}.${savedFbclid}`;
          setCookie('_fbc', fbc, 90);
          lsSet(LS_FBC_BACKUP, fbc);
          try { sessionStorage.setItem('_fb_fbc_session', fbc); } catch { /* silent */ }
        }
      }
    }
  }
}

let _reinforcementStarted = false;

/**
 * Start periodic cookie reinforcement every 30 seconds.
 * Combats ad blockers that periodically clear _fbc/_fbp cookies.
 * Safe to call multiple times — only starts once.
 */
export function startCookieReinforcement() {
  if (_reinforcementStarted) return;
  _reinforcementStarted = true;
  // Run immediately on first call
  reinforceCookies();
  // Then every 30 seconds
  setInterval(reinforceCookies, 30_000);
}

// ============================================
// CURRENCY UTILITIES
// ============================================

export function getCurrentCurrency(): string {
  const country = detectCountry();
  
  const countryToCurrency: Record<string, string> = {
    'US': 'USD', 'CA': 'CAD', 'GB': 'GBP', 'AU': 'AUD', 'NZ': 'NZD', 'IL': 'ILS',
    'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR', 'BE': 'EUR',
    'AT': 'EUR', 'PT': 'EUR', 'IE': 'EUR', 'FI': 'EUR', 'GR': 'EUR',
    'JP': 'JPY', 'KR': 'KRW', 'CN': 'CNY', 'IN': 'INR',
    'CH': 'CHF', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN',
    'SG': 'SGD', 'HK': 'HKD', 'TH': 'THB', 'BR': 'BRL', 'MX': 'MXN',
  };
  
  return countryToCurrency[country] || 'USD';
}

// ============================================
// DATA ENRICHMENT (PII for better EMQ matching)
// ============================================

interface EnrichmentData {
  em?: string;  // email (raw, hashed server-side)
  ph?: string;  // phone (raw)
  fn?: string;  // first name (raw)
  ln?: string;  // last name (raw)
  ct?: string;  // city (raw)
  st?: string;  // state (raw)
  zp?: string;  // zip code (raw)
  ge?: string;  // gender ('f' or 'm')
  [key: string]: string | undefined;
}

/**
 * Enrich user PII for tracking. Call from ANY collection point
 * (popup, survey, form, external integration, etc.).
 * Data is stored in localStorage and automatically included in
 * every subsequent CAPI event + fbq Advanced Matching.
 */
export function enrichUserPII(data: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  state?: string;
  zip?: string;
  gender?: string;
}) {
  try {
    const existing: EnrichmentData = JSON.parse(localStorage.getItem('_fb_user_enrichment') || '{}');
    if (data.email) existing.em = data.email.toLowerCase().trim();
    if (data.phone) existing.ph = data.phone.trim();
    if (data.firstName) existing.fn = data.firstName.trim();
    if (data.lastName) existing.ln = data.lastName.trim();
    if (data.city) existing.ct = data.city.trim();
    if (data.state) existing.st = data.state.trim();
    if (data.zip) existing.zp = data.zip.trim();
    if (data.gender) {
      existing.ge = data.gender.toLowerCase().startsWith('f') ? 'f' : 'm';
    }
    localStorage.setItem('_fb_user_enrichment', JSON.stringify(existing));

    // Re-init fbq with updated Advanced Matching data
    reinitPixelWithPII();
  } catch { /* silent */ }
}

// Backward-compatible wrapper for survey-only gender enrichment
export function enrichUserFromSurvey(data: { gender?: string }) {
  enrichUserPII({ gender: data.gender });
}

// Get raw enrichment email for Identity Bridge cart attributes
export function getEnrichmentEmail(): string {
  try {
    const data = localStorage.getItem('_fb_user_enrichment');
    if (data) {
      const parsed = JSON.parse(data);
      return parsed?.em || '';
    }
  } catch { /* silent */ }
  return '';
}

/**
 * Re-initialize fbq with latest PII for Advanced Matching.
 * Called automatically when enrichUserPII() stores new data.
 */
function reinitPixelWithPII() {
  try {
    if (!window.fbq) return;
    const pixelId = lsGet('_fb_pixel_id');
    if (!pixelId) return;
    const matchingData = getAdvancedMatchingData();
    window.fbq('init', pixelId, matchingData);
    fbDebug('Pixel re-initialized with updated PII:', Object.keys(matchingData));
  } catch { /* silent */ }
}

function getEnrichmentData(): EnrichmentData {
  try {
    const data = localStorage.getItem('_fb_user_enrichment');
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

// ============================================
// CUSTOMER TYPE (New vs Returning)
// ============================================

function isReturningCustomer(): boolean {
  return localStorage.getItem('_fb_has_purchased') === 'true';
}

function markAsPurchased() {
  localStorage.setItem('_fb_has_purchased', 'true');
}

// ============================================
// CONTENTS ARRAY BUILDER
// ============================================

interface ContentItem {
  id: string;
  quantity: number;
  item_price: number;
}

function buildContentsArray(items: Array<{ id: string; quantity: number; price: number }>): ContentItem[] {
  return items.map(item => ({
    id: item.id,
    quantity: item.quantity,
    item_price: item.price,
  }));
}

// ============================================
// GEO CACHE (module-level, populated by FacebookPixel component)
// Solves race condition: first event may fire before get-pixel-config response
// arrives and stores geo data in localStorage. This cache is populated
// synchronously by the component when config data is available.
// ============================================

let _geoCache: { city?: string; region?: string; postal?: string } = {};

// Promise that resolves when geo cache is first populated by setGeoCache().
// Used by waitForGeoData() to delay tracking events until geo data is available.
let _geoCacheResolve: (() => void) | null = null;
const _geoCachePromise = new Promise<void>((resolve) => {
  _geoCacheResolve = resolve;
});

export function setGeoCache(city: string | null, region: string | null, postal: string | null) {
  _geoCache = {
    ...(city ? { city } : {}),
    ...(region ? { region } : {}),
    ...(postal ? { postal } : {}),
  };
  // Signal that geo data is now available
  if (_geoCacheResolve) {
    _geoCacheResolve();
    _geoCacheResolve = null;
  }
}

/**
 * Wait for geo data to be available before building user_data.
 * Resolves immediately if _geoCache or localStorage already has data.
 * Otherwise waits for setGeoCache() (from get-pixel-config response),
 * with a timeout fallback to avoid blocking events indefinitely.
 */
export function waitForGeoData(timeoutMs = 3000): Promise<void> {
  // Already have geo data from module cache or localStorage — no wait needed
  if (_geoCache.city || _geoCache.region || _geoCache.postal ||
      lsGet('_geo_city') || lsGet('_geo_region') || lsGet('_geo_postal')) {
    return Promise.resolve();
  }
  // Wait for setGeoCache() to be called, with timeout fallback
  return Promise.race([
    _geoCachePromise,
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

// ============================================
// BUILD USER DATA FOR CAPI
// ============================================

interface BaseUserData {
  external_id: string;
  country: string;
  fbc: string | null;
  fbp: string | null;
  client_user_agent: string;
  ge?: string;
  [key: string]: string | null | undefined;
}

interface ExtendedUserData extends BaseUserData {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country_code?: string;
}

// Build base user data with browser UA, enrichment, and stored IP
export function buildBaseUserData(): BaseUserData {
  // Bug 1.2: Check URL for fresh fbclid before reading stored values
  initializeFBC();
  // Bidirectional cookie reinforcement: restore cleared cookies from backups
  // AND start periodic reinforcement (30s interval) to combat ad blocker clearing
  reinforceCookies();
  startCookieReinforcement();
  
  const enrichment = getEnrichmentData();
  // Prefer IPv6 over IPv4 for CAPI (Meta prefers IPv6)
  const storedIpV6 = lsGet('_client_ip_v6');
  
  // Use real fbp, or create synthetic fallback to ensure EMQ scoring
  const fbpValue = getOrCreateSyntheticFBP();
  
  const countryCode = detectCountry() || 'US';

  // Bug 5 fix: IPv6 preferred, IPv4 fallback (better than no IP at all)
  const storedIpV4 = lsGet('_client_ip');

  // Geo enrichment: module cache (instant) → localStorage (persisted) → enrichment (user-provided)
  const geoCity = enrichment.ct || _geoCache.city || lsGet('_geo_city') || '';
  const geoRegion = enrichment.st || _geoCache.region || lsGet('_geo_region') || '';
  const geoPostal = enrichment.zp || _geoCache.postal || lsGet('_geo_postal') || '';

  return {
    external_id: getOrCreateVisitorId(),
    country: countryCode,
    country_code: countryCode,
    fbc: getFBC(),
    fbp: fbpValue,
    client_user_agent: navigator.userAgent,
    // PII from enrichment (hashed server-side by CAPI edge function)
    ...(enrichment.em ? { em: enrichment.em } : {}),
    ...(enrichment.ph ? { ph: enrichment.ph } : {}),
    ...(enrichment.fn ? { fn: enrichment.fn } : {}),
    ...(enrichment.ln ? { ln: enrichment.ln } : {}),
    ...(geoCity ? { ct: geoCity } : {}),
    ...(geoRegion ? { st: geoRegion } : {}),
    ...(geoPostal ? { zp: geoPostal } : {}),
    ...(enrichment.ge ? { ge: enrichment.ge } : {}),
    // Always send both IP versions so the CAPI edge function can pick the best one
    // (previously only sent one, losing the fallback when the preferred version was invalid)
    ...(storedIpV6 ? { client_ip_v6: storedIpV6 } : {}),
    ...(storedIpV4 ? { client_ip_v4: storedIpV4 } : {}),
  };
}

// ============================================
// TRAFFIC SOURCE DETECTION
// ============================================

/**
 * Detect traffic source from URL params, cookies, and referrer.
 * Stored once per session in sessionStorage to maintain attribution across pages.
 */
export function detectTrafficSource(): string {
  try {
    // Return cached value if already detected this session
    // EXCEPT: if cached as 'direct' but we now have FB click data,
    // override to 'facebook_paid' (fixes race condition where traffic_source
    // was set before fbclid recovery mechanisms completed)
    const cached = sessionStorage.getItem('_traffic_source');
    if (cached) {
      if (cached === 'direct') {
        // Check if FB signals appeared after the initial detection
        const hasFbSignal = getCookie('_fbc') || lsGet(LS_FBC_BACKUP) || lsGet(LS_LAST_FBCLID);
        if (hasFbSignal) {
          fbDebug('Traffic source recovery: direct → facebook_paid (fbc/fbclid found after initial detection)');
          return cacheTrafficSource('facebook_paid');
        }
        // Check if Google Ads signal appeared after the initial detection
        const hasGclidSignal = lsGet('_ga_last_gclid');
        if (hasGclidSignal) {
          fbDebug('Traffic source recovery: direct → google_paid (gclid found after initial detection)');
          return cacheTrafficSource('google_paid');
        }
      }
      return cached;
    }

    const urlParams = new URLSearchParams(window.location.search);

    // 1. Facebook paid (fbclid in URL or _fbc cookie or localStorage backup)
    if (urlParams.has('fbclid') || getCookie('_fbc') || lsGet(LS_FBC_BACKUP)) {
      return cacheTrafficSource('facebook_paid');
    }

    // 2. Google paid (gclid in URL or localStorage backup)
    const gclid = urlParams.get('gclid');
    if (gclid) {
      try { localStorage.setItem('_ga_last_gclid', gclid); } catch { /* silent */ }
      return cacheTrafficSource('google_paid');
    }

    // 3. UTM source
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    if (utmSource) {
      if (utmSource.toLowerCase() === 'google' && utmMedium?.toLowerCase() === 'cpc') {
        return cacheTrafficSource('google_paid');
      }
      if (/facebook|fb|instagram|ig/i.test(utmSource)) {
        return cacheTrafficSource('facebook_paid');
      }
      return cacheTrafficSource(utmSource.toLowerCase());
    }

    // 4. Facebook/Instagram in-app browser UA (no fbclid but from ad)
    if (/FBAN|FBAV|Instagram/i.test(navigator.userAgent)) {
      return cacheTrafficSource('facebook_paid');
    }

    // 5. Referrer-based detection
    const referrer = document.referrer;
    if (referrer) {
      try {
        const host = new URL(referrer).hostname.toLowerCase();
        // For an ad-driven store, FB/IG referrer = paid (even without fbclid)
        if (/(\.|^)(facebook\.com|fb\.com|instagram\.com|messenger\.com|fbcdn\.net|l\.facebook\.com|lm\.facebook\.com)$/.test(host)) {
          return cacheTrafficSource('facebook_paid');
        }
        if (/(\.|^)(google\.[a-z.]+)$/.test(host)) {
          return cacheTrafficSource('google_organic');
        }
        if (host !== window.location.hostname) {
          return cacheTrafficSource('referral');
        }
      } catch { /* invalid referrer */ }
    }

    // 6. Cross-session recovery: recent fbclid from previous visit
    const lastFbclid = lsGet(LS_LAST_FBCLID);
    if (lastFbclid) {
      return cacheTrafficSource('facebook_paid');
    }

    // 6.5. Cross-session recovery: recent gclid from previous visit
    const lastGclid = lsGet('_ga_last_gclid');
    if (lastGclid) {
      return cacheTrafficSource('google_paid');
    }

    // 7. Direct
    return cacheTrafficSource('direct');
  } catch {
    return 'direct';
  }
}

function cacheTrafficSource(source: string): string {
  try { sessionStorage.setItem('_traffic_source', source); } catch { /* silent */ }
  return source;
}

/**
 * Get the cached traffic source (or detect if not cached).
 */
export function getTrafficSource(): string {
  try {
    return sessionStorage.getItem('_traffic_source') || detectTrafficSource();
  } catch {
    return 'direct';
  }
}

// ============================================
// SEND TO CAPI (Server-side)
// ============================================

async function sendToCAPI(eventData: {
  event_name: string;
  event_time: number;
  event_id: string;
  event_source_url: string;
  user_data?: Record<string, unknown>;
  custom_data?: Record<string, unknown>;
  browser_pixel_fired?: boolean;
  browser_gtag_fired?: boolean;
}, maxRetries = 2) {
  // Wait briefly for index.html IPv6 fetch to complete (started ~2-3s earlier)
  // Waits if we're missing IPv6 (even when IPv4 exists) — Meta prefers IPv6 for matching
  if (!lsGet('_client_ip_v6')) {
    await new Promise(resolve => setTimeout(resolve, 800));
    // Re-read IPs after wait and patch into user_data if found
    const lateIpV6 = lsGet('_client_ip_v6');
    const lateIpV4 = lsGet('_client_ip');
    if (eventData.user_data) {
      if (lateIpV6) {
        eventData.user_data.client_ip_v6 = lateIpV6;
      }
      if (lateIpV4 && !eventData.user_data.client_ip_v4) {
        eventData.user_data.client_ip_v4 = lateIpV4;
      }
    }
  }

  // Final FBC/FBP recovery — ad blockers may have cleared cookies/backups
  // between buildBaseUserData() and this point. Re-check all persistence layers.
  if (eventData.user_data) {
    if (!eventData.user_data.fbc) {
      reinforceCookies();
      const recoveredFbc = getFBC();
      if (recoveredFbc) {
        eventData.user_data.fbc = recoveredFbc;
        fbDebug('FBC recovered before CAPI send:', recoveredFbc);
      }
    }
    if (!eventData.user_data.fbp) {
      reinforceCookies();
      const recoveredFbp = getOrCreateSyntheticFBP();
      if (recoveredFbp) {
        eventData.user_data.fbp = recoveredFbp;
        fbDebug('FBP recovered before CAPI send:', recoveredFbp);
      }
    }
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await supabase.functions.invoke('facebook-capi', {
        body: {
          ...eventData,
          traffic_source: getTrafficSource(),
          is_vpn: lsGet('_is_vpn') === '1',
          is_suspicious: lsGet('_is_suspicious') === '1',
          is_abuser: lsGet('_is_abuser') === '1',
          is_anonymous: lsGet('_is_anonymous') === '1',
          is_bogon: lsGet('_is_bogon') === '1',
          is_icloud_relay: lsGet('_is_icloud_relay') === '1',
          asn_type: lsGet('_asn_type') || null,
          asn_name: lsGet('_asn_name') || null,
        },
      });
      
      if (response.error) {
        console.error('CAPI Error:', response.error);
        return;
      }
      fbDebug('CAPI Success:', response.data);
      return;
    } catch (error) {
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 4000) + Math.random() * 500;
        fbDebug(`CAPI retry ${attempt + 1}/${maxRetries} after ${Math.round(delay)}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        console.error('CAPI Request Failed after retries:', error);
      }
    }
  }
}

// ============================================
// URL SANITIZATION
// ============================================

/**
 * Returns current page URL with sensitive query params stripped.
 * Removes __lovable_token, access_token, token, key, secret, etc.
 */
export function getCleanEventSourceUrl(): string {
  try {
    const url = new URL(window.location.href);
    const sensitiveParams = [
      '__lovable_token', 'access_token', 'token', 'key', 'secret',
      'api_key', 'apikey', 'auth', 'session', 'credential',
    ];
    sensitiveParams.forEach(param => url.searchParams.delete(param));

    // Improvement 1.6: If current URL has no fbclid but landing URL does, append it
    // This ensures downstream events (ViewContent, AddToCart) carry fbclid in event_source_url
    if (!url.searchParams.get('fbclid')) {
      try {
        const landingUrl = sessionStorage.getItem('_fb_landing_url');
        if (landingUrl) {
          const landing = new URL(landingUrl);
          const landingFbclid = landing.searchParams.get('fbclid');
          if (landingFbclid) {
            url.searchParams.set('fbclid', landingFbclid);
          }
        }
      } catch { /* silent */ }
    }

    return url.toString();
  } catch {
    // Fallback: strip query string entirely
    return window.location.origin + window.location.pathname;
  }
}

// ============================================
// SYNTHETIC FBP FALLBACK
// ============================================

/**
 * Generate a synthetic _fbp value when the Pixel SDK hasn't set one.
 * Format matches Meta's spec: fb.1.<creation_time>.<random_number>
 */
function getOrCreateSyntheticFBP(): string {
  // First try real fbp from SDK cookie
  const realFbp = getFBP();
  if (realFbp) return realFbp;

  const syntheticKey = '_fb_synthetic_fbp';
  const existing = lsGet(syntheticKey);
  if (existing) {
    // Ensure cookie exists so SDK can see it and overwrite with real value
    if (!getCookie('_fbp')) {
      setCookie('_fbp', existing, 1); // 24h max — SDK will overwrite with real one
    }
    return existing;
  }

  // Create new synthetic fbp.
  // We write it as a cookie (24h only) so the Pixel SDK can see it and overwrite it with its own real _fbp.
  // This is the "placeholder" pattern — SDK gets the value, or generates its own.
  const syntheticFbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 2147483647 + 1000000000)}`;
  lsSet(syntheticKey, syntheticFbp);
  setCookie('_fbp', syntheticFbp, 1); // 24h only — SDK will overwrite with its real _fbp
  fbDebug('Created synthetic fbp (cookie placeholder):', syntheticFbp);
  return syntheticFbp;
}

// Browser pixel logging is now handled server-side by the facebook-capi edge function
// using the browser_pixel_fired flag. This avoids RLS issues with client-side inserts.

// ============================================
// TRACKING FUNCTIONS
// ============================================

// Track Page View
export async function trackPageView(sharedEventId?: string) {
  // Wait for geo data (from get-pixel-config) before building user_data
  // so PageView has the same enrichment as ViewContent/AddToCart/InitiateCheckout
  await waitForGeoData(2000);
  const eventId = sharedEventId || await generateEventId('PageView', window.location.pathname);
  const eventTime = Math.floor(Date.now() / 1000);
  const userData = buildBaseUserData();
  const cleanUrl = getCleanEventSourceUrl();

  const customData = {
    content_name: document.title,
    content_type: 'page',
  };

  const browserFired = !!window.fbq;
  if (window.fbq) {
    window.fbq('track', 'PageView', customData, { eventID: eventId });
  }

  await sendToCAPI({
    event_name: 'PageView',
    event_time: eventTime,
    event_id: eventId,
    event_source_url: cleanUrl,
    user_data: userData,
    custom_data: customData,
    browser_pixel_fired: browserFired,
    // PageView doesn't go to GA4 MP, so log GA4 browser entry via CAPI edge function
    browser_gtag_fired: !!window.gtag,
  });

  return eventId;
}

// Track View Content (Product Page)
export async function trackViewContent(product: {
  id: string;
  title: string;
  price: number;
  currency: string;
  category?: string;
}, sharedEventId?: string) {
  // Wait for geo data from get-pixel-config before building user_data.
  // On direct product page loads, product API may respond before config,
  // causing ViewContent to fire without geo fields (ct/st/zp).
  await waitForGeoData(3000);
  const eventId = sharedEventId || await generateEventId('ViewContent', product.id);
  const eventTime = Math.floor(Date.now() / 1000);
  const userData = buildBaseUserData();
  const cleanUrl = getCleanEventSourceUrl();

  const contents = buildContentsArray([{ id: product.id, quantity: 1, price: product.price }]);

  const customData = {
    content_ids: [product.id],
    contents: contents,
    content_type: 'product',
    content_name: product.title,
    content_category: product.category || 'Skincare',
    currency: product.currency,
    value: product.price,
    num_items: 1,
  };

  const browserFired = !!window.fbq;
  if (window.fbq) {
    window.fbq('track', 'ViewContent', customData, { eventID: eventId });
  }

  await sendToCAPI({
    event_name: 'ViewContent',
    event_time: eventTime,
    event_id: eventId,
    event_source_url: cleanUrl,
    user_data: userData,
    custom_data: customData,
    browser_pixel_fired: browserFired,
  });

  return eventId;
}

// Track Add to Cart
export async function trackAddToCart(item: {
  productId: string;
  variantId: string;
  title: string;
  price: number;
  currency: string;
  quantity: number;
  category?: string;
}, sharedEventId?: string) {
  await waitForGeoData(3000);
  const eventId = sharedEventId || await generateEventId('AddToCart', `${item.productId}_${Date.now()}`);
  const eventTime = Math.floor(Date.now() / 1000);
  const userData = buildBaseUserData();

  const contents = buildContentsArray([{ id: item.productId, quantity: item.quantity, price: item.price }]);

  const customData = {
    content_ids: [item.productId],
    contents: contents,
    content_type: 'product',
    content_name: item.title,
    content_category: item.category || 'Skincare',
    currency: item.currency,
    value: item.price * item.quantity,
    num_items: item.quantity,
  };

  const browserFired = !!window.fbq;
  if (window.fbq) {
    window.fbq('track', 'AddToCart', customData, { eventID: eventId });
  }

  await sendToCAPI({
    event_name: 'AddToCart',
    event_time: eventTime,
    event_id: eventId,
    event_source_url: getCleanEventSourceUrl(),
    user_data: userData,
    custom_data: customData,
    browser_pixel_fired: browserFired,
  });

  return eventId;
}

// Track Initiate Checkout
export async function trackInitiateCheckout(cart: {
  items: Array<{
    productId: string;
    variantId: string;
    title?: string;
    price: number;
    quantity: number;
  }>;
  totalValue: number;
  currency: string;
  cartId?: string;
}, sharedEventId?: string) {
  await waitForGeoData(3000);
  const eventId = sharedEventId || await generateEventId('InitiateCheckout', cart.cartId || Date.now().toString());
  const eventTime = Math.floor(Date.now() / 1000);
  const userData = buildBaseUserData();

  const contentIds = cart.items.map(item => item.productId);
  const numItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const contentNames = cart.items.map(item => item.title || 'Product').join(', ');
  const contents = buildContentsArray(
    cart.items.map(item => ({ id: item.productId, quantity: item.quantity, price: item.price }))
  );

  const customData = {
    content_ids: contentIds,
    contents: contents,
    content_type: 'product',
    content_name: contentNames,
    content_category: 'Skincare',
    currency: cart.currency,
    value: cart.totalValue,
    num_items: numItems,
  };

  const browserFired = !!window.fbq;
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', customData, { eventID: eventId });
  }

  await sendToCAPI({
    event_name: 'InitiateCheckout',
    event_time: eventTime,
    event_id: eventId,
    event_source_url: getCleanEventSourceUrl(),
    user_data: userData,
    custom_data: customData,
    browser_pixel_fired: browserFired,
  });

  return eventId;
}

// ============================================
// HEADLESS ARCHITECTURE NOTE:
// Purchase events are handled ONLY by:
//   1. Shopify Customer Events (browser-side on checkout page)
//   2. Shopify Webhook (server-side backup)
// This function is kept for reference/testing but should NOT be called
// from the React app in production to avoid duplicate events.
// ============================================

// Track Purchase (DO NOT call from React app - handled by Shopify Customer Events + Webhook)
export async function trackPurchase(order: {
  orderId: string;
  items: Array<{
    productId: string;
    variantId: string;
    title?: string;
    price: number;
    quantity: number;
  }>;
  totalValue: number;
  currency: string;
  createdAt?: number;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  state?: string;
  zip?: string;
  countryCode?: string;
}) {
  const eventId = await generateDeterministicEventId('Purchase', order.orderId, 0);
  const eventTime = order.createdAt || Math.floor(Date.now() / 1000);
  const baseUserData = buildBaseUserData();
  
  const contentIds = order.items.map(item => item.productId);
  const numItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const contentNames = order.items.map(item => item.title || 'Product').join(', ');
  const contents = buildContentsArray(
    order.items.map(item => ({ id: item.productId, quantity: item.quantity, price: item.price }))
  );
  
  const customerType = isReturningCustomer() ? 'returning' : 'new';
  
  const customData = {
    content_ids: contentIds,
    contents: contents,
    content_type: 'product',
    content_name: contentNames,
    content_category: 'Skincare',
    currency: order.currency,
    value: order.totalValue,
    num_items: numItems,
    order_id: order.orderId,
    customer_type: customerType,
  };
  
  // Enhanced user data with customer info
  const userData: ExtendedUserData = {
    ...baseUserData,
    em: order.email,
    ph: order.phone,
    fn: order.firstName,
    ln: order.lastName,
    ct: order.city,
    st: order.state,
    zp: order.zip,
    country_code: order.countryCode,
  };
  
  if (window.fbq) {
    window.fbq('track', 'Purchase', customData, { eventID: eventId });
  }
  
  sendToCAPI({
    event_name: 'Purchase',
    event_time: eventTime,
    event_id: eventId,
    event_source_url: getCleanEventSourceUrl(),
    user_data: userData,
    custom_data: customData,
  });
  
  // Mark as returning customer for future purchases
  markAsPurchased();
}

// ============================================
// ADVANCED MATCHING DATA FOR INIT
// ============================================

export function getAdvancedMatchingData(): Record<string, string> {
  const enrichment = getEnrichmentData();
  // Geo enrichment: module cache → localStorage → enrichment (same chain as buildBaseUserData)
  const geoCity = enrichment.ct || _geoCache.city || lsGet('_geo_city') || '';
  const geoRegion = enrichment.st || _geoCache.region || lsGet('_geo_region') || '';
  const geoPostal = enrichment.zp || _geoCache.postal || lsGet('_geo_postal') || '';

  return {
    external_id: getOrCreateVisitorId(),
    country: detectCountry().toLowerCase(),
    ...(enrichment.em ? { em: enrichment.em } : {}),
    ...(enrichment.ph ? { ph: enrichment.ph } : {}),
    ...(enrichment.fn ? { fn: enrichment.fn } : {}),
    ...(enrichment.ln ? { ln: enrichment.ln } : {}),
    ...(enrichment.ge ? { ge: enrichment.ge } : {}),
    ...(geoCity ? { ct: geoCity } : {}),
    ...(geoRegion ? { st: geoRegion } : {}),
    ...(geoPostal ? { zp: geoPostal } : {}),
  };
}
